import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import MovieIcon from "@mui/icons-material/Movie";
import { useMemo, useRef, useState } from "react";
import { PagedTable, type Column, type SortState } from "../../components/PagedTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  DEFAULT_UPLOAD_QUERY,
  useDeleteUpload,
  useUploadFile,
  useUploads,
  type Upload,
  type UploadQuery,
} from "./api";
import { useDebounced } from "../../hooks/useDebounced";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { formatDateTime } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { mediaTypeLabel } from "../../lib/labels";
import { usePageTitle } from "../../app/usePageTitle";

/** Matches the server, which rejects anything larger. */
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

export function ContentListPage() {
  usePageTitle("Content list");
  const notify = useNotify();
  const [query, setQuery] = useState<UploadQuery>(DEFAULT_UPLOAD_QUERY);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<Upload | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useDebounced(search, 400, (term) =>
    setQuery((previous) => ({ ...previous, search: term, pageNumber: 0 })),
  );

  const uploads = useUploads(query);
  const upload = useUploadFile();
  const remove = useDeleteUpload();

  const columns = useMemo<Column<Upload>[]>(
    () => [
      {
        key: "preview",
        label: "",
        render: (row) =>
          row.mediaType === "IMAGE" ? (
            <Box
              component="img"
              src={row.fileURL}
              alt=""
              loading="lazy"
              sx={{
                width: 56,
                height: 40,
                objectFit: "cover",
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 56,
                height: 40,
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "grid",
                placeItems: "center",
              }}
            >
              <MovieIcon fontSize="small" color="disabled" />
            </Box>
          ),
      },
      { key: "fileName", label: "File name", sortable: true },
      {
        key: "fileURL",
        label: "URL",
        render: (row) => (
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <Link
              href={row.fileURL}
              target="_blank"
              rel="noreferrer"
              sx={{ wordBreak: "break-all" }}
            >
              Open
            </Link>
            <Tooltip title="Copy URL">
              <IconButton
                size="small"
                aria-label={`Copy URL for ${row.fileName}`}
                onClick={async () => {
                  await navigator.clipboard.writeText(row.fileURL);
                  notify.success("URL copied");
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      {
        key: "mediaType",
        label: "Type",
        render: (row) => <Chip size="small" label={mediaTypeLabel(row.mediaType)} />,
      },
      {
        key: "createdAt",
        label: "Uploaded",
        sortable: true,
        render: (row) => formatDateTime(row.createdAt),
      },
      {
        key: "actions",
        label: "Actions",
        align: "right",
        pinned: true,
        render: (row) => (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              aria-label={`Delete ${row.fileName}`}
              onClick={() => setDeleting(row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [notify],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}
      >
        <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
          <Button
            variant="contained"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
          >
            Upload file
          </Button>
          <Typography variant="body2" color="text.secondary">
            Images and video, up to 15 MB
          </Typography>
        </Stack>
        <input
          ref={fileInput}
          type="file"
          hidden
          aria-label="Upload file"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            if (file.size > MAX_UPLOAD_BYTES) {
              notify.error("That file is larger than the 15 MB limit.");
              event.target.value = "";
              return;
            }
            try {
              await upload.mutateAsync({ file });
              notify.success("File uploaded");
            } catch (error) {
              notify.error(errorMessageFor(error) ?? "Unable to process the upload.");
            } finally {
              event.target.value = "";
            }
          }}
        />
      </Stack>

      <Stack direction="row" sx={{ gap: 2, mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <Button
          onClick={() => {
            setSearch("");
            setQuery(DEFAULT_UPLOAD_QUERY);
          }}
        >
          Reset
        </Button>
      </Stack>

      <PagedTable
        columns={columns}
        rows={uploads.data?.data ?? []}
        rowKey={(row) => row.id}
        total={uploads.data?.total ?? 0}
        page={query.pageNumber}
        pageSize={query.pageSize}
        sort={{ sortOn: query.sortOn, sort: query.sort }}
        loading={uploads.isFetching}
        empty={
          <EmptyState
            filtered={query.search !== ""}
            noun="files"
            onClearFilters={() => {
              setSearch("");
              setQuery(DEFAULT_UPLOAD_QUERY);
            }}
          />
        }
        onPageChange={(pageNumber) => setQuery((previous) => ({ ...previous, pageNumber }))}
        onPageSizeChange={(pageSize) =>
          setQuery((previous) => ({ ...previous, pageSize, pageNumber: 0 }))
        }
        onSortChange={(next: SortState) =>
          setQuery((previous) => ({ ...previous, ...next, pageNumber: 0 }))
        }
      />

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.fileName}?`}
        message="Any newsfeed post or message using it will show a broken image. This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            notify.success("File deleted");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Could not delete the file.");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </Box>
  );
}
